import React, { type JSX } from 'react';
import { Box, Divider, styled, useTheme } from '@mui/material';
import { Link, usePage } from '@inertiajs/react';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HomeIcon from '@mui/icons-material/Home';
import RequestQuote from '@mui/icons-material/RequestQuote';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';

import { Firm, User } from '@/types';
import { can, firmRoute, isSuperAdmin } from '@/core/utils';
import { NavigationAccordion } from '@/pages/auth/components/NavigationAccordion';
import { useBoolean } from '@/core/hooks/useBoolean';
import { useNavigationPermissions } from '@/core/hooks/useNavigationPermissions';
import { Permission } from '@/core/const';
import { isCurrentFirmHarvestEnabled } from '@/pages/firms/utils';
import { useFeature } from '@/core/hooks/useFeature';

type Props = {
  handleItemClick: () => void;
};

export const Navigation = ({ handleItemClick }: Props): JSX.Element => {
  const theme = useTheme();
  const {
    component,
    url,
    props: { auth, firm, firms },
  } = usePage<{
    auth: { user: User };
    firm: Firm | null;
    firms: Firm[];
  }>();

  const { user } = auth;

  const [
    isManageUsersRolesTeamsAccordionOpen,
    toggleIsManageUsersRolesTeamsAccordionOpen,
  ] = useBoolean(false);

  const [isTeamManageTeamsAccordionOpen, toggleIsTeamManageTeamsAccordionOpen] =
    useBoolean(false);

  const [
    isManageTaxpayersContactsAccordionOpen,
    toggleIsManageTaxpayersContactsAccordionOpen,
  ] = useBoolean(false);

  const [isFeedbackAccordionOpen, toggleIsFeedbackAccordionOpen] =
    useBoolean(false);

  const {
    canManageUsersRolesTeams,
    canManageAllOrTeamRenewals,
    canTeamManageTeams,
    canManageAllOrTeamInvoices,
    showAdminLabel,
    showStrategyLibrary,
  } = useNavigationPermissions(user);

  const questionnairesEnabled = useFeature('questionnaire_task_type');

  return (
    <Nav>
      <RootNavItem
        icon={<HomeIcon />}
        href={firmRoute('engagements')}
        label="Engagements"
        isActive={component.startsWith('engagements/')}
        onClick={handleItemClick}
      />
      <NavigationAccordion
        isOpen={isManageTaxpayersContactsAccordionOpen}
        toggleIsOpen={toggleIsManageTaxpayersContactsAccordionOpen}
        title="Clients"
        titleIcon={<GroupIcon />}
        children={
          <>
            {(canTeamManageTeams || canManageUsersRolesTeams) && (
              <NavLink
                href={firmRoute('contacts.index')}
                className={component.startsWith('contacts/') ? 'active' : ''}
                onClick={handleItemClick}
              >
                Contacts
              </NavLink>
            )}

            <NavLink
              href={firmRoute('taxpayers.index')}
              className={component.startsWith('taxpayers/') ? 'active' : ''}
              onClick={handleItemClick}
            >
              Clients
            </NavLink>
            {canManageAllOrTeamRenewals && (
              <NavLink
                href={firmRoute('proposals.index')}
                className={component.startsWith('proposals/') ? 'active' : ''}
                onClick={handleItemClick}
              >
                Proposals
              </NavLink>
            )}
          </>
        }
      />
      {canManageAllOrTeamInvoices &&
        isCurrentFirmHarvestEnabled(firms, firm?.pid) && (
          <Box
            sx={{
              minHeight: '48px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 1rem',
              borderRadius: 2,
              '> svg': {
                color: theme.palette.action.active,
              },
              '&.item-active': {
                backgroundColor: '#135D9514',
                color: theme.palette.info.main,
              },
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            className={
              window.location.pathname.includes('invoices') ? 'item-active' : ''
            }
          >
            <RequestQuote />
            <RootNavLink
              href={firmRoute('invoices.index')}
              onClick={handleItemClick}
            >
              Invoices
            </RootNavLink>
          </Box>
        )}
      {(canTeamManageTeams || firm?.isAdvisoryFirm) && (
        <NavigationAccordion
          isOpen={isTeamManageTeamsAccordionOpen}
          toggleIsOpen={toggleIsTeamManageTeamsAccordionOpen}
          title="Manage"
          titleIcon={<SettingsIcon />}
          children={
            <>
              {canTeamManageTeams && (
                <NavLink
                  href={firmRoute('firms.show')}
                  className={component.startsWith('firms/') ? 'active' : ''}
                  onClick={handleItemClick}
                >
                  Firm
                </NavLink>
              )}
              {showStrategyLibrary && (
                <NavLink
                  href={firmRoute('firms.strategy-library.show')}
                  className={
                    component.startsWith('strategy-library/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  Strategy Library
                </NavLink>
              )}
              {/* Internal-only until release: gated to super admins (HWEPD-10110). */}
              {firm?.isAdvisoryFirm && isSuperAdmin(user.roles) && (
                <NavLink
                  href={firmRoute('advisory-opportunities.index')}
                  className={
                    component.startsWith('advisory-opportunities/')
                      ? 'active'
                      : ''
                  }
                  onClick={handleItemClick}
                >
                  Advisory Opportunities
                </NavLink>
              )}
              {firm?.isAdvisoryFirm && (
                <NavLink
                  href={firmRoute('workflow-blueprints.index')}
                  className={
                    component.startsWith('workflows/WorkflowBlueprint') &&
                    !url.startsWith('/admin/')
                      ? 'active'
                      : ''
                  }
                  onClick={handleItemClick}
                >
                  Workflow Templates
                </NavLink>
              )}
              {firm?.isAdvisoryFirm && questionnairesEnabled && (
                <NavLink
                  href={firmRoute('questionnaire-templates.index')}
                  className={
                    component.startsWith('questionnaires/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  Questionnaires
                </NavLink>
              )}
            </>
          }
        />
      )}
      <NavigationAccordion
        isOpen={isFeedbackAccordionOpen}
        toggleIsOpen={toggleIsFeedbackAccordionOpen}
        title="Support"
        titleIcon={<FeedbackIcon />}
        children={
          <>
            <NavLink
              as={'a'}
              target="_blank"
              rel="noopener noreferrer"
              href="https://help.harnesswealth.com"
              onClick={handleItemClick}
            >
              Help center
            </NavLink>
            <NavLink
              href={route('feedback.provide-feedback')}
              className={
                component.startsWith('feedback/ProvideFeedback') ? 'active' : ''
              }
              onClick={handleItemClick}
              data-canny-link=""
            >
              Provide feedback
            </NavLink>
            <NavLink
              href={route('feedback.report-bug')}
              className={
                component.startsWith('feedback/ReportBug') ? 'active' : ''
              }
              onClick={handleItemClick}
              data-canny-link=""
            >
              Report a bug
            </NavLink>
          </>
        }
      />
      {canManageUsersRolesTeams && (
        <NavigationAccordion
          isOpen={isManageUsersRolesTeamsAccordionOpen}
          toggleIsOpen={toggleIsManageUsersRolesTeamsAccordionOpen}
          title={showAdminLabel ? 'Admin' : 'Manage'}
          titleIcon={showAdminLabel ? <HeadsetMicIcon /> : <SettingsIcon />}
          children={
            <>
              {can(user, Permission.MANAGE_TAXPAYERS) && (
                <NavLink
                  href={route('admin.taxpayers.index')}
                  className={
                    component.startsWith('admin/taxpayers/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  Clients
                </NavLink>
              )}

              {can(user, Permission.MANAGE_CONTACTS) && (
                <NavLink
                  href={route('admin.contacts.index')}
                  className={
                    component.startsWith('admin/contacts/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  Contacts
                </NavLink>
              )}

              {can(user, Permission.MANAGE_TAXPAYERS) && (
                <NavLink
                  href={route('admin.efile')}
                  className={
                    component.startsWith('admin/e-file/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  E-file
                </NavLink>
              )}
              <Divider />
              {can(user, Permission.MANAGE_TEAMS) && (
                <NavLink
                  href={route('admin.firms.index')}
                  className={
                    component.startsWith('admin/firms/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  Firms
                </NavLink>
              )}

              {can(user, Permission.MANAGE_ROLES) && (
                <NavLink
                  href={route('roles.index')}
                  className={component.startsWith('roles/') ? 'active' : ''}
                  onClick={handleItemClick}
                >
                  Roles
                </NavLink>
              )}

              {can(user, Permission.MANAGE_TEAMS) && (
                <NavLink
                  href={route('teams.index')}
                  className={component.startsWith('teams/') ? 'active' : ''}
                  onClick={handleItemClick}
                >
                  Teams
                </NavLink>
              )}

              {can(user, Permission.MANAGE_USERS) && (
                <NavLink
                  href={route('users.index')}
                  className={component.startsWith('users/') ? 'active' : ''}
                  onClick={handleItemClick}
                >
                  Users
                </NavLink>
              )}

              {isSuperAdmin(user.roles) && (
                <NavLink
                  href={route('admin.workflow-measurement.index')}
                  className={
                    component.startsWith('admin/workflow-measurement/')
                      ? 'active'
                      : ''
                  }
                  onClick={handleItemClick}
                >
                  Workflow Measurement
                </NavLink>
              )}

              {isSuperAdmin(user.roles) && (
                <NavLink
                  href={route('admin.firm-metrics.index')}
                  className={
                    component.startsWith('admin/firm-metrics/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  Firm Metrics
                </NavLink>
              )}

              {isSuperAdmin(user.roles) && (
                <NavLink
                  href={route('admin.final-returns.index')}
                  className={
                    component.startsWith('admin/final-returns/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  Final Returns
                </NavLink>
              )}

              {isSuperAdmin(user.roles) && (
                <NavLink
                  href={route('admin.audit-log')}
                  className={
                    component.startsWith('admin/audit-log/') ? 'active' : ''
                  }
                  onClick={handleItemClick}
                >
                  Audit Log
                </NavLink>
              )}

              {isSuperAdmin(user.roles) && (
                <NavLink
                  href={route('admin.workflow-blueprint-copy.index')}
                  className={
                    component.startsWith('admin/workflow-blueprint-copy/')
                      ? 'active'
                      : ''
                  }
                  onClick={handleItemClick}
                >
                  Copy Workflow Templates
                </NavLink>
              )}

              {isSuperAdmin(user.roles) && (
                <NavLink
                  href={route('admin.workflow-template-generator.index')}
                  className={
                    component.startsWith('admin/workflow-template-generator/')
                      ? 'active'
                      : ''
                  }
                  onClick={handleItemClick}
                >
                  Generate Workflow Templates
                </NavLink>
              )}
            </>
          }
        />
      )}
    </Nav>
  );
};

const Nav = styled('nav')`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-grow: 1;
  padding: 0 0.5rem;

  > a:not(:first-of-type) {
    margin-top: 0.5rem;
  }
`;

const NavLink = styled(Link)(
  ({ theme }) => `
  color: ${theme.palette.secondary.main};
  font-size: 0.85rem;
  font-weight: 400;
  text-decoration: none;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0rem 0.5rem 4rem;
  border-radius: 0.25rem;
  margin-bottom: 0.1rem;

  &:hover {
    background-color:  ${theme.palette.action.hover};
  }

  &.active {
    background-color: #135D9514;
    color: ${theme.palette.info.main};
   }
`,
);

const RootNavLink = styled(NavLink)`
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 500;
  padding-left: 1.5rem;
  margin-bottom: 0;

  &:hover {
    background-color: transparent;
  }
`;

const RootNavItem = ({
  icon,
  href,
  label,
  isActive,
  onClick,
}: {
  icon: React.ReactNode;
  href: string;
  label: string;
  isActive: boolean;
  onClick: () => void;
}): JSX.Element => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minHeight: '48px',
        display: 'flex',
        alignItems: 'center',
        padding: '0 1rem',
        borderRadius: 2,
        '> svg': {
          color: theme.palette.action.active,
        },
        '&.item-active': {
          backgroundColor: '#135D9514',
          color: theme.palette.info.main,
        },
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
      className={isActive ? 'item-active' : ''}
    >
      {icon}
      <RootNavLink href={href} onClick={onClick}>
        {label}
      </RootNavLink>
    </Box>
  );
};
